import { useQuery } from "@tanstack/react-query";
import type { AiCall, AiFilters, AiGroup, AiModels, AiTotals, AiUsageView, ModelPrice, RelayReadiness } from "@studio-features/meta-crew/llm-data";
import type { UsageExploreQuery, UsageExploreView } from "@studio-features/meta-crew/usage-explorer-data";
import { api } from "./api";

/**
 * AI 키·모델 설정과 사용량 (docs/meta-crew-llm-relay-design.md).
 * 2026-10-21 단순화: 모델은 키를 등록·연결 확인해 가져온 목록에서 키마다 고르고, 기본 모델은 그 합집합에서 고른다 (수동 입력 없음).
 */

export type Relay = { ok: true; model: string; effort: string } | { ok: false; reason: string };
type Setting = { key: string; value: unknown };

/** 서버 키 한 행 (값은 hint 만). */
export type LlmKey = {
  id: string;
  label: string;
  hint: string;
  status: "active" | "disabled" | "invalid";
  priority: number;
  monthlyBudgetUsd: number | null;
  lastUsedAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  createdAt: string;
  /** 연결 확인 때 가져온 모델 (provider = 공급자 목록, builtin = 모델 읽기 권한이 없어 Codex 기본 목록) */
  models: string[];
  modelsSource: "provider" | "builtin" | null;
  /** 이 키로 쓰도록 고른 모델 */
  enabledModels: string[];
  month: { costUsd: number; calls: number; tokens: number };
};
export type LlmModels = AiModels & { defaultValid: boolean };
export type LlmKeysData = { keys: LlmKey[]; models: LlmModels; relay: Relay };
export type LlmSettings = {
  enabled: boolean;
  effort: string;
  prices: Record<string, ModelPrice>;
  priceSource: Record<string, { source: "auto" | "manual"; checkedAt: string | null }>;
};

export const llmKeys = {
  keys: ["admin", "llm", "keys"] as const,
  models: ["admin", "llm", "models"] as const,
  settings: ["admin", "settings"] as const,
  usage: ["admin", "llm", "usage"] as const,
};
export const llmInvalidate = [llmKeys.keys, llmKeys.models, llmKeys.settings];

export const readiness = (r: Relay | undefined): RelayReadiness =>
  !r ? { ok: false, reason: "확인 중", model: "", effort: "" } : r.ok ? { ok: true, reason: "사용 가능", model: r.model, effort: r.effort || "기본" } : { ok: false, reason: r.reason, model: "", effort: "" };

function settingsFrom(list: Setting[]): LlmSettings {
  const get = (k: string) => list.find((s) => s.key === k)?.value;
  return {
    enabled: get("llm_relay_enabled") !== false,
    effort: String(get("llm_relay_reasoning_effort") ?? ""),
    prices: (get("llm_prices") as Record<string, ModelPrice> | undefined) ?? {},
    priceSource: (get("llm_prices_meta") as LlmSettings["priceSource"] | undefined) ?? {},
  };
}

/** 시스템 설정 › AI 키: 키(가져온 모델·고른 모델) + 고를 수 있는 모델·기본 모델 + 준비 상태 + 설정(사용 여부·추론 강도·단가). */
export function useAiKeySettings() {
  const keys = useQuery({ queryKey: llmKeys.keys, queryFn: () => api.get<LlmKeysData>("/admin/llm/keys") });
  const settings = useQuery({ queryKey: llmKeys.settings, queryFn: () => api.get<Setting[]>("/admin/settings"), select: settingsFrom });
  return { keys, settings };
}

/** 고를 수 있는 모델 + 모델별 단가 (100만 토큰당 USD, 단가 없으면 null). 2026-10-21 서버가 함께 내려준다. */
/** 서버 /admin/llm/models: 허용 모델별 단가 (USD / 100만 토큰, 없으면 null). 시안 AiModels.prices 와 같은 모양. */
export type PricedModels = AiModels & { prices?: Record<string, ModelPrice | null> };

/** 작업 등록·원고함 보내기의 모델 선택 (직원만). */
export function useAiModels(enabled: boolean) {
  return useQuery({ queryKey: llmKeys.models, queryFn: () => api.get<PricedModels & { relay: Relay }>("/admin/llm/models"), enabled });
}

/* ---------------- 사용량 ---------------- */

type ServerCall = Omit<AiCall, "customerId" | "customerName" | "machineId" | "machineLabel" | "model"> & {
  customerId: string | null;
  customerName: string | null;
  machineId: string | null;
  machineLabel: string | null;
  model: string | null;
};
type ServerGroup = AiTotals & { key: string };

/** 서버 nullable 필드를 화면 표시값으로 (필터 ID 는 만들지 않는다). */
export const toCall = (c: ServerCall): AiCall => ({
  ...c,
  customerId: c.customerId ?? "",
  // 고객사 없는 호출 = 관리자(메타크루) 작업 (원고 정형화 등, 원고 소유 설계 2-2). 고객사 ID 가 있는데 이름이 없으면 삭제된 고객사
  customerName: c.customerName ?? (c.customerId ? "삭제됨" : "관리자"),
  machineId: c.machineId ?? "",
  machineLabel: c.machineLabel ?? "미기록",
  model: c.model ?? "미기록",
});

const qs = (f: AiFilters, extra: Record<string, string | number> = {}) => {
  const p: Record<string, string | number> = { from: f.from, to: f.to, ...extra };
  if (f.customerId !== "all") p.customerId = f.customerId;
  if (f.machineId !== "all") p.machineId = f.machineId;
  if (f.model !== "all") p.model = f.model;
  if (f.mode !== "all") p.mode = f.mode;
  if (f.status !== "all") p.status = f.status;
  return (
    "?" +
    Object.entries(p)
      .filter(([, v]) => v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join("&")
  );
};

const sum = (rows: ServerGroup[]): AiTotals =>
  rows.reduce<AiTotals>(
    (t, r) => ({
      calls: t.calls + r.calls,
      failed: t.failed + r.failed,
      inputTokens: t.inputTokens + r.inputTokens,
      cachedTokens: t.cachedTokens + r.cachedTokens,
      outputTokens: t.outputTokens + r.outputTokens,
      reasoningTokens: t.reasoningTokens + r.reasoningTokens,
      costUsd: t.costUsd + r.costUsd,
      unpriced: t.unpriced + r.unpriced,
    }),
    { calls: 0, failed: 0, inputTokens: 0, cachedTokens: 0, outputTokens: 0, reasoningTokens: 0, costUsd: 0, unpriced: 0 },
  );

/** 모니터링 › LLM 사용량: 합계·일별(방식별)·묶음 4종·호출 이력 (더 보기 = limit 증가). */
export function useAiUsage(f: AiFilters, limit: number, lists: { customers: { id: string; label: string }[]; machines: { id: string; label: string }[] }) {
  return useQuery({
    queryKey: [...llmKeys.usage, f, limit, lists.customers.length, lists.machines.length],
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<AiUsageView> => {
      const group = (g: string, extra: Record<string, string> = {}) => api.get<ServerGroup[]>(`/admin/llm/usage${qs(f, { groupBy: g, ...extra })}`);
      const modes = f.mode === "all" ? (["local", "relay"] as const) : ([f.mode] as ("local" | "relay")[]);
      const [byMode, perMode, customer, machine, model, key, calls] = await Promise.all([
        group("mode"),
        Promise.all(modes.map((m) => group("day", { mode: m }).then((rows) => [m, rows] as const))),
        group("customer"),
        group("machine"),
        group("model"),
        group("key"),
        api.page<ServerCall>(`/admin/llm/calls${qs(f, { offset: 0, limit })}`),
      ]);
      const days = new Map<string, { day: string; local: number; relay: number }>();
      for (const [m, rows] of perMode)
        for (const r of rows) {
          const d = days.get(r.key) ?? { day: r.key, local: 0, relay: 0 };
          d[m] += r.calls;
          days.set(r.key, d);
        }
      const allModels = await api.get<ServerGroup[]>(`/admin/llm/usage${qs({ ...f, model: "all" }, { groupBy: "model" })}`);
      return {
        totals: sum(byMode),
        days: [...days.values()].sort((a, b) => a.day.localeCompare(b.day)),
        groups: { customer: customer as AiGroup[], machine: machine as AiGroup[], model: model as AiGroup[], key: key as AiGroup[] },
        calls: calls.items.map(toCall),
        total: calls.total,
        customers: lists.customers,
        machines: lists.machines,
        models: allModels.map((r) => r.key).filter((k) => k !== "-"),
      };
    },
  });
}

/**
 * 사용량 탐색 (요청서 28절, 시안 66ecd3a UsageExplorer). 서버 집계(total·groups·series)를 그대로 넘기고 화면에서 다시 더하지 않는다.
 * 쿼리가 바뀌면 키가 바뀌어 늦게 온 이전 응답은 화면에 쓰이지 않는다.
 */
export function useUsageExplore(q: UsageExploreQuery) {
  return useQuery({
    queryKey: [...llmKeys.usage, "explore", q],
    placeholderData: (prev) => prev,
    queryFn: () => {
      const { dims, ...rest } = q;
      const p = Object.entries({ ...rest, dims: dims.join(",") })
        .filter(([, v]) => v !== undefined && v !== "")
        .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
        .join("&");
      return api.get<UsageExploreView>(`/admin/llm/usage/explore?${p}`);
    },
  });
}

/** 한국 날짜 YYYY-MM-DD (오늘 - days). */
export function kstDay(offsetDays = 0): string {
  return new Date(Date.now() + 9 * 3_600_000 - offsetDays * 86_400_000).toISOString().slice(0, 10);
}
